import { InputAdornment, TextFieldProps } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { FaXMarkLightSvgIcon } from '../../assets/icons/svgs';
import { borderRadius } from '../../theme/borderRadius';
import { colors } from '../../theme/colors';
import spacing, { isMobileScreen } from '../../theme/spacing';
import { fontSize } from '../../theme/typography';
import LabelText from './LabelText';
import {
  formikFieldBaseStyles,
  formikTopLabelAbsoluteStyles,
  formikTopLabelStyles,
} from './styles';

interface AdditionalProps {
  maxLength?: number;
  containerStyle?: React.CSSProperties;
  nonAbsoluteLabel?: boolean;
  variant?: 'standard' | 'outlined';
  style?: React.CSSProperties;
  onClick?: () => void;
}

type TextFieldFormikProps = TextFieldProps & AdditionalProps;

const useStyles = createUseStyles({
  hideDefaultClearIcon: {
    '&::-webkit-search-cancel-button': {
      display: 'none',
      WebkitAppearance: 'none',
    },
    '&::-ms-clear': {
      display: 'none',
    },
    '&::-ms-reveal': {
      display: 'none',
    },
  },
  textFieldRootContained: {
    backgroundColor: colors.backgroundSuperLight,
    borderRadius: borderRadius.large,
    '&:hover': {
      backgroundColor: colors.backgroundSuperLight,
    },
    '& div::before': {
      borderBottomColor: `transparent !important`,
    },
    '& div::after': {
      borderBottomColor: `transparent !important`,
    },
    '& label': {
      color: `${colors.textGreyMedium} !important`,
      fontSize: fontSize.input,
      paddingLeft: isMobileScreen ? 6 : 5,
    },
    '& input, & textarea': {
      color: `${colors.textPrimary} !important`,
      fontSize: fontSize.input,
    },
    '& textarea': {
      paddingLeft: spacing.xs,
    },
    '& input': {
      paddingLeft: spacing.medium,
    },
    '&.error': {
      border: `1px solid ${colors.redLight}`,
    },
  },
  textFieldRootOutlined: {
    ...formikFieldBaseStyles,
    '& .Mui-disabled': {
      background: 'transparent',
    },
    '& div::before': {
      borderBottomColor: `transparent !important`,
    },
    '& div::after': {
      borderBottomColor: `transparent !important`,
    },
    '& label': {
      color: `${colors.textGreyMedium} !important`,
      fontSize: fontSize.input,
      paddingLeft: isMobileScreen ? 6 : 5,
    },
    '& input, & textarea': {
      color: `${colors.textPrimary} !important`,
      fontSize: fontSize.input,
    },
    '& textarea': {
      paddingLeft: spacing.xs,
    },
    '& input': {
      paddingLeft: spacing.medium,
    },
    '&.error': {
      border: `1px solid ${colors.redLight}`,
    },
  },
});

export default function TextFieldFormik(props: TextFieldFormikProps) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const {
    nonAbsoluteLabel,
    containerStyle,
    variant = 'standard',
    error,
    ...otherProps
  } = props;

  const rootClassName = `${
    variant === 'standard'
      ? classes.textFieldRootContained
      : classes.textFieldRootOutlined
  } ${error ? 'error' : ''}`;

  const ClearSearchButton = () => {
    const isSearchClearShown =
      (props.type === 'search' ||
        (props.type === 'text' && props.id === 'search')) &&
      props.value;

    if (!isSearchClearShown) {
      return null;
    }

    return (
      <InputAdornment
        position="end"
        style={{
          position: 'relative',
          right: -8,
        }}
      >
        <IconButton
          onClick={() => {
            if (props.onChange) {
              const clearedInputValueEvent = {
                target: {
                  name: props.name,
                  value: '',
                },
              } as React.ChangeEvent<HTMLInputElement>;
              props.onChange(clearedInputValueEvent);
            }
          }}
        >
          <FaXMarkLightSvgIcon
            size={fontSize.xl}
            style={{
              color: colors.textPrimary,
            }}
          />
        </IconButton>
      </InputAdornment>
    );
  };

  return (
    <div
      style={{
        position: 'relative',
        ...containerStyle,
      }}
    >
      {error && (
        <LabelText
          text={
            props.helperText
              ? String(props.helperText)
              : t('common.fillFieldToContinue')
          }
          error={true}
          style={
            nonAbsoluteLabel
              ? formikTopLabelStyles
              : formikTopLabelAbsoluteStyles
          }
        />
      )}
      <TextField
        {...otherProps}
        error={false}
        helperText={null}
        fullWidth
        variant="filled"
        sx={{
          '& .MuiInputLabel-shrink': {
            fontSize: fontSize.md,
          },
          '& .MuiInputBase-input.Mui-disabled': {
            WebkitTextFillColor: colors.textGreyMedium,
          },
        }}
        slotProps={{
          input: {
            className:
              props.type === 'search' ? classes.hideDefaultClearIcon : '',
            style: {
              color: colors.textPrimary,
            },
            inputProps: {
              maxLength: props.maxLength,
            },
            endAdornment: <ClearSearchButton />,
          },
        }}
        classes={{
          root: rootClassName,
        }}
      />
    </div>
  );
}
